import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { siteConfig } from '../config/site';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Industries', path: '/industries' },
    { name: 'Leadership', path: '/leadership' },
    { name: 'Vision', path: '/vision' },
    { name: 'Social Impact', path: '/social-impact' },
    { name: 'Financials', path: '/financials' },
    { name: 'Corporate Brief', path: '/corporate-brief' },
    { name: 'Dashboard', path: 'https://arholdings-dashboard.streamlit.app/', external: true },
    { name: 'Contact', path: '/contact' }
  ];

  const logoSrc = theme === 'dark' ? siteConfig.logo.dark : siteConfig.logo.light;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-[#0B1F3A] text-white text-center text-xs py-1.5 tracking-wider hidden sm:block">
        {siteConfig.companyName} · {siteConfig.domain}
      </div>

      <nav
        className={`transition-all duration-300 ${
          isScrolled
            ? 'bg-background/98 backdrop-blur-sm border-b border-border shadow-sm'
            : 'bg-background border-b border-border'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-[72px]">
            <Link to="/" className="flex items-center gap-4">
              <img
                src={logoSrc}
                alt={siteConfig.shortName}
                className="h-11 w-auto object-contain"
                onError={(e) => { e.currentTarget.src = siteConfig.logo.light; }}
              />
              <div className="hidden md:block border-l border-border pl-4">
                <div className="text-sm font-semibold text-foreground leading-tight">Holdings Group</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Corporate</div>
              </div>
            </Link>

            <div className="hidden xl:flex items-center gap-6">
              {navLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-[#B8941F] transition-colors"
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-xs font-medium uppercase tracking-wide transition-colors ${
                      location.pathname === link.path
                        ? 'text-[#B8941F]'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              )}
              <button
                onClick={toggleTheme}
                className="p-2 border border-border hover:border-[#B8941F] transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>

            <div className="xl:hidden flex items-center gap-2">
              <button onClick={toggleTheme} className="p-2" aria-label="Toggle theme">
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2">
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="xl:hidden bg-background border-t border-border px-6 py-4 space-y-1">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2.5 text-sm font-medium text-muted-foreground"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2.5 text-sm font-medium ${
                    location.pathname === link.path ? 'text-[#B8941F]' : 'text-muted-foreground'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
